import Button from "@ingka/button";
import Checkbox from "@ingka/checkbox";
import SSRIcon from "@ingka/ssr-icon";
import noticeRedSmall from "@ingka/ssr-icon/paths/notice-red-small";
import documentIcon from "@ingka/ssr-icon/paths/document";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import classNames from "classnames";
import { AuthenticatedImage } from "../AuthenticatedResource/AuthenticatedImage";
import { useMetadataFields } from "@/components/MetadataFields/hooks";
import { useStore } from "@/services/store";
import { useShallow } from "zustand/react/shallow";
import { isUploadFinalized } from "@/components/MetadataFields/formFieldsAccessRules";

type Props = {
  assetId: number;
  isActive: boolean;
  isChecked: boolean;
  allChecked: boolean;
  onAssetClick: (assetId: number) => void;
  onAssetChecked: (assetId: number) => void;
  onRemoveAsset: () => Promise<void>;
};

const AssetItemView = ({
  assetId,
  isActive,
  isChecked,
  allChecked,
  onAssetClick,
  onAssetChecked,
  onRemoveAsset,
}: Props) => {
  const { hasAssetValidationErrors } = useMetadataFields();
  const { uploadState, assetExtension } = useStore(
    useShallow((state) => ({
      uploadState: state.uploadState,
      assetExtension: state.assets.find((a) => a.id === assetId)?.extension,
    })),
  );

  const isUploadDraft = uploadState === "DRAFT";

  const renderMediaElement = () => {
    if (assetExtension === "docx" || assetExtension === "xlsx") {
      return (
        <div className="flex size-full min-h-[3rem] items-center">
          <SSRIcon
            paths={documentIcon}
            aria-label="Document preview"
            className="size-8 text-neutral-500"
          />
        </div>
      );
    }
    return (
      <AuthenticatedImage
        assetId={assetId}
        className="max-h-[3rem] object-contain"
      />
    );
  };

  return (
    <div className="flex items-center justify-between">
      <div
        className={classNames("asset-item-view", {
          "active-border": isActive && isChecked && !allChecked,
        })}
      >
        {isUploadDraft && (
          <Button
            size="small"
            type="tertiary"
            iconOnly
            aria-label="Remove asset"
            ssrIcon={trashCan}
            onClick={onRemoveAsset}
          />
        )}
        <button
          className="asset-item__button flex flex-1 items-center justify-between"
          onClick={() => onAssetClick(assetId)}
        >
          {renderMediaElement()}
        </button>
        <div className="flex items-center">
          {hasAssetValidationErrors(assetId) && (
            <SSRIcon
              paths={noticeRedSmall}
              className="mr-space25 mt-[0.375rem]"
            />
          )}
          <Checkbox
            id={String(assetId)}
            value=""
            checked={isChecked}
            aria-label={`Select asset ${assetId}`}
            disabled={isUploadFinalized({ uploadState })}
            onChange={() => onAssetChecked(assetId)}
          />
        </div>
      </div>
    </div>
  );
};

export default AssetItemView;
